import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem } from '@carbon/react';
import styles from './analyze-breadcrumb.module.scss';

/**
 * One breadcrumb crumb — exactly one kind: a route link (`href`, client-side via
 * `<Link>`), a client action (`onClick`, e.g. a drill-down pop), or the current page
 * (neither — always the last item). The `never` guards make the compiler enforce that a
 * crumb can't be both a link and an action.
 */
export type AnalyzeCrumb =
  | { label: string; href: string; onClick?: never }
  | { label: string; onClick: () => void; href?: never }
  | { label: string; href?: never; onClick?: never };

interface AnalyzeBreadcrumbProps {
  /** Ordered crumbs; the LAST is the current location (rendered non-clickable). */
  items: AnalyzeCrumb[];
}

/**
 * The single breadcrumb for the Analyze Value Drivers flow — one source of truth so the
 * three pages (and the Results drill-down) share an identical trail. `href` crumbs are
 * client-side router links (preserving the flow's React Query cache), `onClick` crumbs
 * pop a drill level in place, and the last crumb is the current location.
 *
 * @param {AnalyzeBreadcrumbProps} props - The ordered crumbs (last = current).
 * @return {JSX.Element} The breadcrumb.
 */
export function AnalyzeBreadcrumb({ items }: AnalyzeBreadcrumbProps) {
  const lastIndex = items.length - 1;

  return (
    <Breadcrumb noTrailingSlash className={styles.breadcrumb}>
      {items.map((item, index) => {
        if (index === lastIndex) {
          return (
            <BreadcrumbItem key={index} isCurrentPage>
              {item.label}
            </BreadcrumbItem>
          );
        }
        if (item.href) {
          return (
            <BreadcrumbItem key={index}>
              <Link to={item.href}>{item.label}</Link>
            </BreadcrumbItem>
          );
        }
        if (item.onClick) {
          return (
            <BreadcrumbItem key={index}>
              <button
                type="button"
                className={styles.crumbButton}
                onClick={item.onClick}
              >
                {item.label}
              </button>
            </BreadcrumbItem>
          );
        }
        // A non-terminal crumb with neither link nor action: plain, non-interactive.
        return <BreadcrumbItem key={index}>{item.label}</BreadcrumbItem>;
      })}
    </Breadcrumb>
  );
}
